import React, { useEffect, useRef, useState } from 'react';
import isArray from 'lodash/isArray';
import isEmpty from 'lodash/isEmpty';
import classNames from 'classnames';
import styled from 'styled-components';
import ModalDialog from '../../../shared/components/modal/ModalDialog';
import {
    AUTOMATIC_ORDERING_ENABLED,
    AUTOMATIC_ORDERING_STATE,
    GENERATION_INTERVAL,
    GENERATION_INTERVAL_NEVER,
    mapCopyFormula,
    mapMaxCopiesValueToKey
} from '../constants';
import {
    ReserveSettingsContainer,
    RSColumn,
    RSFieldValue,
    RSLoanFormat,
    RSMaxCopiesRow
} from './ReserveSettingsComponents';
import { ReserveCheckBox } from './ReserveCheckBox';
import { ReserveListDefaultSettingsOut } from '../../../store/api';
import { Trans, useTranslation } from 'react-i18next';
import { animateHintScroll } from './ReserveUtils';
import { ReserveHint } from './ReserveHint';
import isEqual from 'lodash/isEqual';
import { LOANFORMAT_EAUDIOBOOKS, LOANFORMAT_EBOOKS } from '../../../shared/loanFormat/constants';

const RSDMaxCopiesRow = styled.div`
    font-size: 14px;
    margin: 8px 0;
`;

type Props = {
    readonly title: string;
    readonly show: boolean;
    readonly settings: ReserveListDefaultSettingsOut;
    readonly onAbort: () => void;
    readonly onSuccessSettingsPopup: (message: string) => void;
};

export const ReserveListSettingsPopup = (props: Props) => {
    const { t } = useTranslation();
    const requestSettingsContainer = useRef<HTMLDivElement>(null);
    const userInputContainer = useRef<HTMLDivElement>(null);
    const requestSettingsTextArea = useRef<any>(null);
    const [requestChange, setRequestChange] = useState(false);
    const [message, setMessage] = useState('');
    const [isHintShown, setIsHintShown] = useState(false);

    useEffect(() => {
        if (isHintShown) {
            if (requestSettingsContainer.current) {
                animateHintScroll(requestSettingsContainer.current);
            }
        }
        if (requestChange) {
            if (userInputContainer.current) {
                animateHintScroll(userInputContainer.current);
            }
        }
    }, [isHintShown, requestChange]);

    function closeDialog() {
        setRequestChange(false);
        props.onAbort();
    }

    function submitChangeRequest() {
        if (isEmpty(message)) {
            return;
        }
        setRequestChange(false);
        setMessage('');
        props.onSuccessSettingsPopup(message);
    }

    const showEmailRecipients = isArray(props.settings.emails) && (props.settings.emails?.length ?? 0) > 0;
    const generationDay = props.settings.generationDay ? t(`day.${props.settings.generationDay?.toLowerCase()}`) : null;
    const generationInterval = props.settings.generationInterval;
    const automaticOrderingEnabled = props.settings.approvalType === AUTOMATIC_ORDERING_ENABLED;
    const ebookSettings = props.settings.loanFormatDefaults?.[LOANFORMAT_EBOOKS] ?? {};
    const ebookCopyFormulaKey = ebookSettings?.copyFormula;
    const ebookCopyFormula = mapCopyFormula(ebookCopyFormulaKey);
    const ebooksIsChecked = ebookSettings.selected;
    const ebookReservesPerCopy = ebookSettings.reserveRatio;
    const ebookMaxCopies = mapMaxCopiesValueToKey(ebookSettings.maxCopies);
    const ebookAccountName = ebookSettings.accountName || t('reserveReports.defaultSettings.notSpecified');
    const ebookRowClass = classNames({ 'disabled-text': !ebooksIsChecked });
    const eaudiobookSettings = props.settings?.loanFormatDefaults?.[LOANFORMAT_EAUDIOBOOKS] ?? {};
    const eaudiobooksIsChecked = eaudiobookSettings.selected;
    const eaudiobookCopyFormulaKey = eaudiobookSettings.copyFormula;
    const eaudiobookCopyFormula = mapCopyFormula(eaudiobookCopyFormulaKey);
    const eaudiobookMaxCopies = mapMaxCopiesValueToKey(eaudiobookSettings.maxCopies);
    const eaudiobookReservesPerCopy = eaudiobookSettings.reserveRatio;
    const eaudiobookAccountName = eaudiobookSettings.accountName || t('reserveReports.defaultSettings.notSpecified');
    const eaudiobookRowClass = classNames({ 'disabled-text': !eaudiobooksIsChecked });
    const marginRight = { marginRight: '15px' };

    const eBookCopyFormulaValue = ebookCopyFormula?.value.toLowerCase();
    const eAudiobookCopyFormulaValue = eaudiobookCopyFormula?.value.toLowerCase();

    const EAudioBooksMaxCopies = () => (
        <RSFieldValue>
            {t(`reserveReports.defaultSettings.maxCopies.${eaudiobookMaxCopies}`, {
                defaultValue: eaudiobookMaxCopies
            })}
        </RSFieldValue>
    );
    const EBooksMaxCopies = () => (
        <RSFieldValue>
            {t(`reserveReports.defaultSettings.maxCopies.${ebookMaxCopies}`, {
                defaultValue: ebookMaxCopies
            })}
        </RSFieldValue>
    );

    const EbookReservesPerCopy = () => <RSFieldValue>{ebookReservesPerCopy}</RSFieldValue>;
    const EAudiobookReservesPerCopy = () => <RSFieldValue>{eaudiobookReservesPerCopy}</RSFieldValue>;
    const EbookCopyFormula = () => (
        <RSFieldValue>
            {t(`reserveReports.defaultSettings.copyFormulaKey.${eBookCopyFormulaValue}`, {
                defaultValue: ebookCopyFormula?.value
            })}
        </RSFieldValue>
    );
    const EAudiobookCopyFormula = () => (
        <RSFieldValue>
            {t(`reserveReports.defaultSettings.copyFormulaKey.${eAudiobookCopyFormulaValue}`, {
                defaultValue: eaudiobookCopyFormula?.value
            })}
        </RSFieldValue>
    );

    const generationIntervalValue = GENERATION_INTERVAL[generationInterval as keyof typeof GENERATION_INTERVAL];

    const GenerationInterval = () => (
        <span className="popuptable-data">
            {t(`reserveReports.generateNewReport.generationInterval.${generationIntervalValue}`, {
                generationDay
            })}
        </span>
    );

    const approvalType =
        AUTOMATIC_ORDERING_STATE[props.settings.approvalType as keyof typeof AUTOMATIC_ORDERING_STATE]?.toLowerCase();

    const AutomaticTopUps = () => (
        <span className="popuptable-data">{t(`reserveReports.defaultSettings.automaticTopUps.${approvalType}`)}</span>
    );

    const EmailRecipients = () => (
        <span className="popuptable-data">
            {showEmailRecipients
                ? props.settings.emails?.map((email, i) => {
                      return (
                          <div key={i}>
                              {email}
                              <br />
                          </div>
                      );
                  })
                : 'None'}
        </span>
    );

    const EBookAccountName = () => <RSFieldValue>{ebookAccountName}</RSFieldValue>;
    const EAudiobookAccountName = () => <RSFieldValue>{eaudiobookAccountName}</RSFieldValue>;

    const SeparateReportsPerFormat = () => (
        <span className="popuptable-data">
            {props.settings.separatedLoanFormatGeneration
                ? t('yourLocalContent.yourTitles.yes')
                : t('yourLocalContent.yourTitles.no')}
        </span>
    );

    return (
        <ModalDialog
            show={props.show}
            titleClassName="black-title"
            onCloseButtonClick={() => {
                props.onAbort();
                setRequestChange(false);
                setIsHintShown(false);
            }}
            title={props.title}
            width={750}
            overwriteWidth={true}
            wrapClassName="reserve-settings-modal js-reserve-settings-height-resize"
            preventBodyScrolling={true}
        >
            <>
                <h2 className="green-title">{t('reserveReports.defaultSettings.generalSettings')}</h2>
                <table className="popuptable">
                    <tbody>
                        <tr>
                            <td>
                                <Trans
                                    i18nKey="reserveReports.defaultSettings.generateReserveReports"
                                    components={{ 1: <GenerationInterval /> }}
                                />
                            </td>
                        </tr>
                        {isEqual(generationInterval, GENERATION_INTERVAL_NEVER) ? null : (
                            <>
                                <tr>
                                    <td>
                                        <Trans
                                            i18nKey="reserveReports.defaultSettings.emailRecipients"
                                            components={{ 1: <EmailRecipients /> }}
                                        />
                                    </td>
                                </tr>
                                <tr>
                                    <td>
                                        <Trans
                                            i18nKey="reserveReports.defaultSettings.separateReportsPerFormat"
                                            components={{ 1: <SeparateReportsPerFormat /> }}
                                        />
                                    </td>
                                </tr>
                                <tr>
                                    <td>
                                        <Trans
                                            i18nKey="reserveReports.defaultSettings.automaticTopUps.label"
                                            components={{ 1: <AutomaticTopUps /> }}
                                        />
                                    </td>
                                </tr>
                            </>
                        )}
                    </tbody>
                </table>
            </>

            <ReserveSettingsContainer>
                <h2 className="green-title">{t('reserveReports.defaultSettings.defaultReserveReportSettings')}</h2>
                <RSColumn>
                    <RSLoanFormat>
                        <ReserveCheckBox
                            isEnabled={false}
                            isChecked={ebooksIsChecked}
                            text={t('menu.shop.ebooksMain')}
                        />
                    </RSLoanFormat>
                    <RSDMaxCopiesRow className={ebookRowClass}>
                        <Trans
                            i18nKey="reserveReports.reservesPerCopyLimit"
                            components={{ 1: <EbookReservesPerCopy /> }}
                        />
                    </RSDMaxCopiesRow>
                    <RSDMaxCopiesRow className={ebookRowClass}>
                        <Trans i18nKey="reserveReports.maxCopiesInCollection" components={{ 1: <EBooksMaxCopies /> }} />
                    </RSDMaxCopiesRow>
                    <RSDMaxCopiesRow className={ebookRowClass}>
                        <Trans
                            i18nKey="reserveReports.calculationOfAdditionalCopies"
                            components={{ 1: <EbookCopyFormula /> }}
                        />
                    </RSDMaxCopiesRow>
                    {automaticOrderingEnabled ? (
                        <RSMaxCopiesRow style={{ display: 'flex' }} className={ebookRowClass}>
                            <Trans
                                i18nKey="reserveReports.defaultSettings.account"
                                components={{ 1: <EBookAccountName /> }}
                            />
                        </RSMaxCopiesRow>
                    ) : null}
                </RSColumn>
                <RSColumn>
                    <RSLoanFormat>
                        <ReserveCheckBox
                            isEnabled={false}
                            isChecked={eaudiobooksIsChecked}
                            text={t('menu.shop.eaudiobooksMain')}
                        />
                    </RSLoanFormat>
                    <RSDMaxCopiesRow className={eaudiobookRowClass}>
                        <Trans
                            i18nKey="reserveReports.reservesPerCopyLimit"
                            components={{ 1: <EAudiobookReservesPerCopy /> }}
                        />
                    </RSDMaxCopiesRow>
                    <RSDMaxCopiesRow className={eaudiobookRowClass}>
                        <Trans
                            i18nKey="reserveReports.maxCopiesInCollection"
                            components={{ 1: <EAudioBooksMaxCopies /> }}
                        />
                    </RSDMaxCopiesRow>
                    <RSDMaxCopiesRow className={eaudiobookRowClass}>
                        <Trans
                            i18nKey="reserveReports.calculationOfAdditionalCopies"
                            components={{ 1: <EAudiobookCopyFormula /> }}
                        />
                    </RSDMaxCopiesRow>
                    {automaticOrderingEnabled ? (
                        <RSMaxCopiesRow style={{ display: 'flex' }} className={eaudiobookRowClass}>
                            <Trans
                                i18nKey="reserveReports.defaultSettings.account"
                                components={{ 1: <EAudiobookAccountName /> }}
                            />
                        </RSMaxCopiesRow>
                    ) : null}
                </RSColumn>
            </ReserveSettingsContainer>
            <div ref={userInputContainer}>
                {requestChange ? (
                    <textarea
                        ref={requestSettingsTextArea}
                        className="settingsTextarea"
                        value={message}
                        onChange={e => setMessage(e.target.value)}
                        placeholder={t('reserveReports.defaultSettings.settingTextArea')}
                    />
                ) : (
                    <div ref={requestSettingsTextArea} />
                )}
            </div>
            <div
                style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    flexFlow: 'row-reverse',
                    flexWrap: 'wrap'
                }}
            >
                {requestChange ? (
                    <div>
                        <a
                            style={marginRight}
                            className="bt bt-bordered-simple large no-transition"
                            onClick={closeDialog}
                        >
                            {t('cancel')}
                        </a>
                        <a className="bt bt-green large" onClick={submitChangeRequest}>
                            {t('reserveReports.defaultSettings.sendRequest')}
                        </a>
                    </div>
                ) : (
                    <div ref={requestSettingsContainer}>
                        <a
                            style={marginRight}
                            className="bt bt-bordered-simple large no-transition"
                            onClick={() => setRequestChange(true)}
                        >
                            {t('reserveReports.defaultSettings.requestSettingsChange')}
                        </a>
                        <a className="bt bt-green large" onClick={props.onAbort}>
                            {t('reserveReports.defaultSettings.close')}
                        </a>
                    </div>
                )}
                <ReserveHint
                    toggleHint={() => setIsHintShown(!isHintShown)}
                    isHintShown={isHintShown}
                    borderOverflow={'36px'}
                    lineHeight={'21px'}
                />
            </div>
        </ModalDialog>
    );
};
